import React from "react";
import { format, parseISO } from "date-fns";
import { AlertTriangle, X } from "lucide-react";

export interface ConflictRow {
    scheduleId: number;
    employeeName: string;
    groupName: string;
    date: string; // 'yyyy-MM-dd'
    shiftName: string;
    timeRange: string;
    severity: "red" | "amber";
    messages: string[];
}

export interface ConflictListModalProps {
    open: boolean;
    rows: ConflictRow[];
    onClose: () => void;
}

const ConflictListModal: React.FC<ConflictListModalProps> = ({ open, rows, onClose }) => {
    if (!open) return null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1400]"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] m-4 flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg">
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">
                            Conflicts in this view ({rows.length})
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Close"
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-2">
                    {rows.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-8">
                            No conflicts in the current view.
                        </p>
                    ) : (
                        rows.map((row) => (
                            <div
                                key={row.scheduleId}
                                className={`relative w-full px-3 py-2 rounded-lg border shadow-sm overflow-hidden ${
                                    row.severity === "red"
                                        ? "border-red-200 bg-red-50"
                                        : "border-amber-200 bg-amber-50"
                                }`}
                            >
                                <div
                                    className={`absolute left-0 top-0 bottom-0 w-1.5 ${
                                        row.severity === "red" ? "bg-red-500" : "bg-amber-500"
                                    }`}
                                />
                                <div className="pl-3">
                                    <div className="flex items-center justify-between gap-2 min-w-0">
                                        <span className="font-medium text-gray-900 truncate">
                                            {row.employeeName}
                                            {row.groupName ? (
                                                <span className="text-gray-500 font-normal"> · {row.groupName}</span>
                                            ) : null}
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-600 mt-0.5">
                                        {format(parseISO(row.date), "EEE, MMM d")} · {row.shiftName}
                                        {row.timeRange ? ` · ${row.timeRange}` : ""}
                                    </div>
                                    <div className="mt-1 space-y-0.5">
                                        {row.messages.map((message, i) => (
                                            <div key={i} className="text-xs text-gray-700">
                                                {message}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="flex justify-end gap-3 p-6 bg-gray-50 rounded-b-xl border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConflictListModal;
